import FocusManager from '../utils/focus-manager';
import ScrollManager from '../utils/scroll-manager';

type ModalState = 'open'|'close'|'toggle';

export interface ModalHTMLElement extends HTMLElement {
    open() : void;
    close() : void;
    toggle() : void;
    on(state: ModalState, fn: CallableFunction) : void;
    isOpen() : boolean;
    isIntentExitModal() : boolean;
    isIntentScrollModal() : boolean;
}

export default function initModals() : void {
    const $modals = document.querySelectorAll<ModalHTMLElement>('[data-modal-id]');

    $modals.forEach($modal => {
        new Modal($modal);
    });
}

class Modal {
    $modal: ModalHTMLElement;
    callbacks: {[key in ModalState]: CallableFunction[]} = { open: [], close: [], toggle: [] };
    intentExitAlreadyClosed = false;
    intentScrollAlreadyClosed = false;

    constructor($modal: ModalHTMLElement) {
        this.$modal = $modal;

        this.$modal.open = this.open;
        this.$modal.close = this.close;
        this.$modal.toggle = this.toggle;
        this.$modal.on = this.on;
        this.$modal.isOpen = this.isOpen;
        this.$modal.isIntentExitModal = this.isIntentExitModal;
        this.$modal.isIntentScrollModal = this.isIntentScrollModal;

        // Remove the display none preventing the first transition animation
        this.$modal.style.display = '';

        // Open, Close, Toggle the modal when clicking on a [data-modal-{type}] button
        ['open', 'close', 'toggle'].forEach(type => {
            Array.from(document.querySelectorAll(`[data-modal-${type}="${this.$modal.dataset.modalId}"]`)).forEach($button => {
                $button.addEventListener('click', this[type]);
            });
        });

        // Close the modal when clicking outside
        this.$modal.addEventListener('click', (evt) => {
            if (evt.target !== this.$modal) {
                return;
            }

            this.close();
        });

        // Close the modal when pressing Escape key
        document.addEventListener('keyup', (evt) => {
            if (this.isOpen() && evt.key === 'Escape') {
                this.close();
            }
        });

        // Open the modal when cursor move above the window
        if (this.isIntentExitModal()) {
            document.addEventListener('mouseout', (evt) => {
                if (!this.intentExitAlreadyClosed && !evt.relatedTarget && !this.isOpen()) {
                    this.intentExitAlreadyClosed = true;
                    this.open();
                }
            });
        }

        // Open the modal when scroll pos reaches the required pos
        if (this.isIntentScrollModal()) {
            const expectedScrollPos = parseInt(this.$modal.dataset.intentScrollModal);
            setTimeout(() => {
                window.addEventListener('scroll', () => {
                    if (!this.intentScrollAlreadyClosed && !this.isOpen() && window.scrollY >= expectedScrollPos) {
                        this.intentScrollAlreadyClosed = true;
                        setTimeout(() => {
                            this.open();
                        }, 500);
                    }
                });
            }, 500);
        }
    }

    /**
     * Open the modal
     * 
     * @return {void}
     */
    open = () : void => {
        if (this.isOpen()) {
            return;
        }

        this.$modal.classList.add('open');
        ScrollManager.preventScroll(true);
        FocusManager.trapFocus(this.$modal);

        // Execute registered callbacks
        this.callbacks.open.forEach(fn => fn());
    }

    /**
     * Close the modal
     * 
     * @return {void}
     */
    close = () : void => {
        if (!this.isOpen()) {
            return;
        }

        this.$modal.classList.remove('open');
        ScrollManager.preventScroll(false);
        FocusManager.untrapFocus();

        // Execute registered callbacks
        this.callbacks.close.forEach(fn => fn());
    }

    /**
     * Toggle the modal
     * 
     * @return {void}
     */
    toggle = () : void => {
        if (this.isOpen()) {
            this.open();
        } else {
            this.close();
        }

        // Execute registered callbacks
        this.callbacks.toggle.forEach(fn => fn());
    }

    /**
     * Register a callback on an state
     * 
     * @param {ModalState} state
     * @param {CallableFunction} fn
     * 
     * @return {void}
     */
    on = (state: ModalState, fn: CallableFunction) : void => {
        this.callbacks[state].push(fn);
    }

    /**
     * Return if the modal is opened
     * 
     * @return {boolean}
     */
    isOpen = () : boolean => this.$modal.classList.contains('open');
    
    /**
     * Return if the modal is an intent exit modal
     * 
     * @return {boolean}
     */
    isIntentExitModal = () : boolean => this.$modal.hasAttribute('data-intent-exit-modal');
    
    /**
     * Return if the modal is an intent scroll modal
     * 
     * @return {boolean}
     */
    isIntentScrollModal = () : boolean => this.$modal.hasAttribute('data-intent-scroll-modal');

    /**
     * Return the modal ID
     * 
     * @return {string}
     */
    getModalId = () : string => this.$modal.getAttribute('data-modal-id');
}
